import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DepartmentSchema, ReferenceMasterSchema, type DepartmentDto } from '@taqon/contracts';
import { z } from 'zod';
import { useSearchParams } from 'react-router';
import { getData, getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { SearchFilterBar } from '../../shared/ui/filters';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { ImportButton } from '../../shared/import/ImportButton';
import { exportHref } from '../../shared/export/export-href';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ConfirmDialog, Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { SearchPicker } from '../finance/lookups';

/**
 * Departments, cost centres and locations (ERD §4.2; FRD-CORE-002, CORE-004, CORE-005, CORE-007):
 * the list — searched, filtered and paged on the server, with the filters in the URL so a refresh
 * or a dashboard link keeps them — each row opening the record's own page, and, where the session
 * may create, the create form. Codes are supplied by the business (FRD-CORE-035), so nothing is
 * generated here.
 */
/** Every master's row; only a department carries a parent. */
export const MasterRowSchema = ReferenceMasterSchema.extend({
  parentDepartmentId: z.string().nullable().optional(),
});

export type MasterRow = z.infer<typeof MasterRowSchema>;

const PAGE_SIZE = 50;

export interface MasterKind {
  readonly title: string;
  readonly singular: string;
  readonly description: string;
  /** The API collection. */
  readonly path: string;
  /** The web list page; a record's page is `<route>/<id>`. */
  readonly route: string;
  readonly queryKey: string;
  /** The record type, whose `export` permission shows the export (FRD-CORE-040). */
  readonly recordType: string;
  readonly createCapability: string;
  readonly changeCapability: string;
  /** Departments sit in a hierarchy; the others are flat. */
  readonly hierarchical: boolean;
}

export const DEPARTMENTS: MasterKind = {
  title: 'Departments',
  singular: 'department',
  description: 'The organizational structure used across the system.',
  path: '/api/v1/organization/departments',
  route: '/organization/departments',
  recordType: 'organization.department',
  queryKey: 'departments',
  createCapability: 'organization.department:create',
  changeCapability: 'organization.department:change',
  hierarchical: true,
};

export const COST_CENTRES: MasterKind = {
  title: 'Cost centres',
  singular: 'cost centre',
  description: 'Cost centres used as a financial dimension, distinct from departments (D7).',
  path: '/api/v1/organization/cost-centers',
  route: '/organization/cost-centres',
  recordType: 'organization.cost_center',
  queryKey: 'cost-centers',
  createCapability: 'organization.cost_center:create',
  changeCapability: 'organization.cost_center:change',
  hierarchical: false,
};

export const LOCATIONS: MasterKind = {
  title: 'Locations',
  singular: 'location',
  description: 'Business locations.',
  path: '/api/v1/organization/locations',
  route: '/organization/locations',
  recordType: 'organization.location',
  queryKey: 'locations',
  createCapability: 'organization.location:create',
  changeCapability: 'organization.location:change',
  hierarchical: false,
};

export function MastersPage({ kind }: { kind: MasterKind }) {
  const session = useSession();
  const canCreate = hasCapability(session.data, kind.createCapability);
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const status = params.get('status') ?? '';
  const term = params.get('search') ?? '';
  const [search, setSearch] = useState(term);
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: 'status' | 'search' | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: 'code',
  });
  if (status !== '') query.set('filter[status]', status);
  if (term !== '') query.set('filter[search]', term);
  const rows = useQuery({
    queryKey: [kind.queryKey, 'list', query.toString()],
    queryFn: () => getPage(`${kind.path}?${query.toString()}`, MasterRowSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title={kind.title}
        description={kind.description}
        actions={
          <>
            <ExportButton recordType={kind.recordType} href={exportHref(kind.path, query)} />
            <ImportButton recordType={kind.recordType} />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setOpen(true);
                }}
              >
                New {kind.singular}
              </Button>
            )}
          </>
        }
      />
      <SearchFilterBar
        aria-label={`${kind.title} filters`}
        label="Search by code or name"
        value={search}
        onChange={setSearch}
        onBlur={() => {
          if (search.trim() !== term) set('search', search.trim());
        }}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={status}
          onChange={(e) => {
            set('status', e.target.value);
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </SearchFilterBar>
      {rows.isError && <ErrorState message={rows.error.message} />}
      <Section>
        {rows.isPending && <LoadingState label={`Loading ${kind.title.toLowerCase()}`} />}
        {rows.data !== undefined && rows.data.items.length === 0 && (
          <EmptyState title={`No ${kind.title.toLowerCase()} found`} />
        )}
        {rows.data !== undefined && rows.data.items.length > 0 && (
          <>
            <Table size="small" aria-label={kind.title}>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  {kind.hierarchical && <TableCell>Parent</TableCell>}
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.data.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`${kind.route}/${row.id}`}>{row.code}</RecordLink>
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    {kind.hierarchical && (
                      <TableCell>
                        {row.parentDepartmentId == null ? (
                          '—'
                        ) : (
                          <DepartmentRef id={row.parentDepartmentId} />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <StatusChip status={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={rows.data.total}
              noun="record(s)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {open && (
        <MasterDialog
          kind={kind}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </Stack>
  );
}

/** A department named by its code and name, linked to its page (read with the same permission). */
export function DepartmentRef({ id }: { id: string }) {
  const department = useQuery({
    queryKey: ['departments', 'one', id],
    queryFn: () => getData(`${DEPARTMENTS.path}/${id}`, DepartmentSchema),
  });
  const text =
    department.data === undefined ? id : `${department.data.code} — ${department.data.name}`;
  return <RecordLink to={`${DEPARTMENTS.route}/${id}`}>{text}</RecordLink>;
}

/**
 * Departments by code or name, searched on the server; only active ones may be a new parent
 * (FRD-CORE-007). A choice that would make a cycle is refused by the API (FRD-CORE-004).
 */
function ParentPicker({
  value,
  onChange,
}: {
  value: DepartmentDto | null;
  onChange: (value: DepartmentDto | null) => void;
}) {
  return (
    <SearchPicker
      queryKey="departments-parent"
      label="Parent department"
      schema={DepartmentSchema}
      value={value}
      onChange={onChange}
      path={(term) =>
        `${DEPARTMENTS.path}?pageSize=25&sort=code&filter[status]=active` +
        (term === '' ? '' : `&filter[search]=${encodeURIComponent(term)}`)
      }
      describe={(d) => `${d.code} — ${d.name}`}
      helperText="Leave empty for a top-level department."
    />
  );
}

/**
 * Create (no `row`) or edit a master: its name and, for a department, its parent. The code is the
 * permanent identifier and is never amended (FRD-CORE-035). If-Match carries the version shown, so
 * a change made on stale data is refused. Status has its own confirmed action.
 */
export function MasterDialog({
  kind,
  row,
  parent: initialParent = null,
  onClose,
}: {
  kind: MasterKind;
  row?: MasterRow;
  parent?: DepartmentDto | null;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState(row?.name ?? '');
  const [parent, setParent] = useState<DepartmentDto | null>(initialParent);
  const body: Record<string, string | null> = {};
  if (row === undefined) {
    body.code = code.trim();
    body.name = name.trim();
    if (kind.hierarchical && parent !== null) body.parentDepartmentId = parent.id;
  } else {
    // Only what changed is sent: the API refuses a no-op change.
    if (name.trim() !== row.name) body.name = name.trim();
    if (kind.hierarchical && (parent?.id ?? null) !== (row.parentDepartmentId ?? null)) {
      body.parentDepartmentId = parent?.id ?? null;
    }
  }
  const save = useMutation({
    mutationFn: () =>
      row === undefined
        ? postJson(kind.path, body, MasterRowSchema, session.data?.csrfToken ?? '')
        : patchJson(
            `${kind.path}/${row.id}`,
            body,
            MasterRowSchema,
            session.data?.csrfToken ?? '',
            row.version,
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [kind.queryKey] });
      // The record page's history shows the change at once.
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={row === undefined ? `New ${kind.singular}` : `Edit ${kind.singular} ${row.code}`}
      submitLabel={row === undefined ? 'Create' : 'Save changes'}
      canSubmit={
        name.trim() !== '' &&
        (row !== undefined || code.trim() !== '') &&
        Object.keys(body).length > 0
      }
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {row === undefined ? (
        <TextField
          label="Code"
          required
          autoFocus
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
          }}
          helperText="Upper case, up to 20 characters, starting with a letter."
        />
      ) : (
        <TextField label="Code" value={row.code} disabled />
      )}
      <TextField
        label="Name"
        required
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      {kind.hierarchical && <ParentPicker value={parent} onChange={setParent} />}
    </FormDialog>
  );
}

/** Deactivate or activate a master, after a confirmation (FRD-CORE-007). */
export function MasterStatusDialog({
  kind,
  row,
  onClose,
}: {
  kind: MasterKind;
  row: MasterRow;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = row.status === 'active' ? 'inactive' : 'active';
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${kind.path}/${row.id}`,
        { status: next },
        MasterRowSchema,
        session.data?.csrfToken ?? '',
        row.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [kind.queryKey] });
      // The record page's history shows the change at once.
      await queryClient.invalidateQueries({ queryKey: ['platform', 'audit'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      title={next === 'inactive' ? `Deactivate ${row.code}` : `Activate ${row.code}`}
      message={
        next === 'inactive'
          ? `Deactivate ${row.name}? It stays on the records that already use it but cannot be chosen for new work.`
          : `Activate ${row.name} again?`
      }
      confirmLabel={next === 'inactive' ? 'Deactivate' : 'Activate'}
      pending={save.isPending}
      error={save.isError ? save.error.message : null}
      onConfirm={() => {
        save.mutate();
      }}
      onClose={onClose}
    />
  );
}
